import cx from 'classnames';
import moment from 'moment-timezone';
import React from 'react';
import { Field } from 'react-final-form';
import { FieldArray } from 'react-final-form-arrays';

import { getTimezoneDescription, getUserTime, isRedZone, resolveTimezone } from '@/core/time';

import DateTimePicker from '../DateTimePicker';
import Link from '../Link';
import styles from './ConsultationDates.module.scss';

const s = styles as typeof styles & { timezoneStatic: string };

interface ConsultationDatesProps {
  name: string;
  userName?: string;
  userTimezone?: string;
  viewer: { timezone?: string | null };
  label?: string;
  displayWarning?: boolean;
  style?: React.CSSProperties;
  dateOnly?: boolean;
  hideTimezoneLink?: boolean; // Hide the "Change My Timezone" link and show static "Your Timezone" text
}

const ConsultationDates = ({
  name,
  userName,
  userTimezone,
  viewer,
  label,
  displayWarning,
  style,
  dateOnly,
  hideTimezoneLink,
}: ConsultationDatesProps): JSX.Element => {
  // The picker draws the viewer's wall clock, so the earliest selectable day is "today" in
  // the viewer's timezone, expressed in the same frame the picker reads. Using the browser's
  // midnight offered a day already past for the viewer — or blocked one still to come.
  // Resolve the zone once and hand the same one to the picker: an unrecognised profile
  // timezone reads as UTC here but falls back to the browser's zone there, and the two halves
  // then disagree about the frame — silently swallowing clicks on days that are still valid.
  const viewerTimezone = resolveTimezone(viewer.timezone);
  const minDate = moment(moment.tz(viewerTimezone).format('YYYY-MM-DD')).toDate();
  return (
    <div style={style}>
      <div className={s.label}>{label}</div>

      <FieldArray name={name}>
        {({ fields }) =>
          fields.map((name, index) => {
            const value = fields.value[index];
            const timezoneClassName = cx(s.timezone, {
              [s.timezoneRed]: isRedZone(value, userTimezone),
            });

            return (
              <div key={name} className={s.picker}>
                <Field
                  component={DateTimePicker}
                  minDate={minDate}
                  name={name}
                  index={index}
                  placeholder={`Option ${index + 1}`}
                  style={{ flex: '1' }}
                  inline
                  timezone={viewerTimezone}
                  displayWarning={displayWarning}
                  dateOnly={dateOnly}
                />
                {!dateOnly && (
                  <div className={timezoneClassName}>
                    {value && getUserTime(value, userTimezone, userName)}
                  </div>
                )}
              </div>
            );
          })
        }
      </FieldArray>

      {!dateOnly && (
        <div className={s.changeTimezone}>
          {hideTimezoneLink ? (
            <span className={s.timezoneStatic}>
              Your Timezone {getTimezoneDescription(viewer.timezone)}
            </span>
          ) : (
            <Link newTab className={s.changeTimezoneLink} to="/settings/communication">
              Change My Timezone {getTimezoneDescription(viewer.timezone)}
            </Link>
          )}
        </div>
      )}
    </div>
  );
};

export default ConsultationDates;
